import React, {
  useState,
  useEffect,
  lazy,
  Suspense,
  useCallback,
  useMemo,
} from "react";
import { Box, Typography } from "@mui/material";
import GettingReady from "../../components/controls/Modals/gettingReady";
import { RootState, AppDispatch } from "../../store/store";
import { resetState, setToggleSideBar } from "../../store/slices/filterSlice";
import { useDispatch, useSelector } from "react-redux";
import CustomTabs from "../../components/controls/CustomTabs";
import useIsMobile from "../../hooks/useIsMobile.hook";
import {
  exportCsvStream,
  formatRowsForPDF,
  formatValueForCSV,
  generateExportFileName,
  getSessionData,
  searchRecordData,
  buildSearchBody,
  formatRecordData,
  STATUS_TO_TAB,
} from "../../utils/helper";
import { ENDPOINT } from "../../api/endpoint";
import fetchWrapper from "../../api/fetchWrapper";
import { ToastHelper } from "../../utils/toastHelper";
import FilterList from "../../components/Filter/FilterList";
import jsPDF from "jspdf";
import "jspdf-autotable";
// import ExcelJS from "exceljs";
// import { saveAs } from "file-saver";
import { useGenerateSampleData } from "./useGenerateSampleData.hook";
import { useExportRecordData } from "./useExportRecordsData.hook";
import {
  defaultAutoTableOptions,
  // defaultAutoTableOptions,
  // defaultJSPDFOptions,
  requiredColumns,
} from "../../utils/constants";

import { useRecordData } from "./useRecordData.hook";
import RecordNavbar from "../../components/RecordNavbar";
import { setRefresh, setSelectedRecord } from "../../store/slices/recordSlice";
import { tabsConfig } from "./tabConfig";
import { Row } from "../../interface";
import { useCountryGroupMembers } from "../../hooks/useCountryGroupMembers.hook";
//import autoTable from "jspdf-autotable";

const ExportRecord = lazy(
  () => import("../../components/controls/Modals/exportRecords")
);
const GenerateSample = lazy(
  () => import("../../components/controls/Modals/generateSample")
);
const RecordDetails = lazy(
  () => import("../../components/controls/RecordDetails")
);

const Record: React.FC = () => {
  const toastHelper = useMemo(() => new ToastHelper(), []);
  const [open, setOpen] = useState<boolean>(true);
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { selectedRecordId, refresh } = useSelector(
    (state: RootState) => state.record
  );
  const { selectedFilter } = useSelector((state: RootState) => state.filter);
  const countryGroupMembers = useCountryGroupMembers();
  const [exportOpen, setExportOpen] = useState(false);
  const userInfo = getSessionData();
  const [activeTab, setActiveTab] = useState<number>(1);
  const [downloadInProgress, setDownloadInProgress] = useState<boolean>(false);

  const { getSampleRecordData } = useGenerateSampleData();
  const { exportRecordData } = useExportRecordData(activeTab);

  const [limits, setLimits] = useState({
    all: [0, 100],
    nr: [0, 100], // Needs review
    nrk: [0, 100], // No Risk
  });

  const {
    recordData: allRecord,
    isError: allError,
    isLoading: allLoading,
    poll: allPoll,
    totalCount: allTotal,
    warning: allWarning,
  } = useRecordData({
    needsReview: false,
    dataLimit: limits.all,
  });

  const {
    recordData: nrRecord,
    isError: nrError,
    isLoading: nrLoading,
    poll: nrPoll,
    totalCount: nrTotal,
    warning: nrWarning,
  } = useRecordData({
    needsReview: true,
    dataLimit: limits.nr,
  });

  const {
    recordData: nrkRecord,
    isError: nrkError,
    isLoading: nrkLoading,
    poll: nrkPoll,
    totalCount: nrkTotal,
    warning: nrkWarning,
  } = useRecordData({
    noRisk: true,
    dataLimit: limits.nrk,
  });

  //Reset the limit if the filter changes
  useEffect(() => {
    setLimits({ all: [0, 100], nr: [0, 100], nrk: [0, 100] });
  }, [selectedFilter]);

  //Handle error and  zero records for all tab
  const handleRecordErrors = useCallback(
    (isLoading: boolean, isError: boolean, warning: any) => {
      if (!isLoading) {
        if (isError) {
          toastHelper.showToast({ severity: "error", content: warning });
        } else if (warning) {
          toastHelper.showToast({
            severity: "warn",
            title: "No records found",
            content: warning,
          });
        }
      }
    },
    [toastHelper]
  );

  useEffect(() => {
    return handleRecordErrors(allLoading, allError, allWarning);
  }, [allLoading, allError, allWarning, handleRecordErrors]);
  useEffect(
    () => handleRecordErrors(nrLoading, nrError, nrWarning),
    [nrLoading, nrError, nrWarning, handleRecordErrors]
  );
  useEffect(
    () => handleRecordErrors(nrkLoading, nrkError, nrkWarning),
    [nrkLoading, nrkError, nrkWarning, handleRecordErrors]
  );

  const handleFilterSubmit = () => {
    if (!isMobile) dispatch(setToggleSideBar(true));
    setOpen(false);
  };

  const handleGenerateSummary = () => {
    setIsOpen(true);
  };

  const [searchData, setSearchData] = useState<string>("");
  const [backendResults, setBackendResults] = useState<any[]>([]);

  const allRecordWithSearch = useMemo(
    () => searchRecordData(allRecord, searchData) ?? [],
    [allRecord, searchData]
  );
  const nrRecordWithSearch = useMemo(
    () => searchRecordData(nrRecord, searchData) ?? [],
    [nrRecord, searchData]
  );
  const nrkRecordWithSearch = useMemo(
    () => searchRecordData(nrkRecord, searchData) ?? [],
    [nrkRecord, searchData]
  );

  // Called when user presses Enter or clicks Search
  const handleSearchSubmit = useCallback(async () => {
    const term = searchData.trim();
    setBackendResults([]);
    if (!term) return;

    // Check all local tabs, not just the active one
    const hasLocal = nrRecordWithSearch.length > 0 || allRecordWithSearch.length > 0 || nrkRecordWithSearch.length > 0;
    if (hasLocal) {
      // Auto-switch to the first tab that has local results
      if (nrRecordWithSearch.length > 0) setActiveTab(0);
      else if (allRecordWithSearch.length > 0) setActiveTab(1);
      else setActiveTab(2);
      return;
    }

    try {
      const body = buildSearchBody(
        selectedFilter,
        term,
        [0, 100],
        countryGroupMembers
      );
      const res = await fetchWrapper(ENDPOINT.SEARCH_RECORDS, { method: "POST", body });
      if (res?.data && !res?.message) {
        const formatted = formatRecordData(res.data);
        setBackendResults(formatted);
        // Auto-switch to the tab matching the first result's status
        if (formatted.length > 0) {
          const firstStatus = formatted[0]?.status;
          const tabIndex = STATUS_TO_TAB[firstStatus];
          if (tabIndex !== undefined) setActiveTab(tabIndex);
        }
      }
    } catch (err) {
      console.error("Backend search failed:", err);
    }
  }, [searchData, nrRecordWithSearch, allRecordWithSearch, nrkRecordWithSearch, selectedFilter, countryGroupMembers]);

  const convertDataGridToCSV = async (fileName: string) => {
    const data = await exportRecordData();
    await exportCsvStream<Row>(fileName, requiredColumns, data, formatValueForCSV);
  };

  const downloadCSV = async () => {
    if(Number(currentTabTotal)  > 300000) {
       toastHelper.showToast({
        severity: "warn",
        title: "Export limit reached",
        content: `Your record selection ${currentTabTotal} rows exceeds the export limit of 300,000 rows. Refine your filters and try again.`,
        options: {
          position: "top-center",
          autoClose: 5000,
        }
      });
      return;
    }
    try {
      toastHelper.showToast({
        severity: "info",
        title: "Processing",
        content: "Export in progress...",
        options: {
          position: "top-center",
          autoClose: 500,
        }
      });
      setDownloadInProgress(true);
      const fileName = generateExportFileName("csv");
      await convertDataGridToCSV(fileName);
      setDownloadInProgress(false);
      toastHelper.showToast({
        severity: "success",
        content: `Export successful: ${fileName}`,
        options: {
            position: "top-center",
            autoClose: 500,
        }
      });

    } catch (error) {
      console.error(error);
      setDownloadInProgress(false);
      toastHelper.showToast({
        severity: "error",
        content: "Export failed due to server error. Please try again.",
        options: {
          position: "top-center",
          autoClose: 5000,
        }
      });

    }
  };

  const saveBlob = (blob: Blob, suggestedName: string)=> {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = suggestedName;
      a.click();
      URL.revokeObjectURL(a.href);
  }

    const currentTabTotal = useMemo(() => {
    switch (activeTab) {
      case 0:
        return nrTotal ;
      case 1:
        return allTotal;
      case 2:
        return nrkTotal ;
      default:
        return 0;
    }
  }, [activeTab, nrTotal, allTotal, nrkTotal]);

  const downloadPDF = async (selectedHeaders: string[]) => {
    if(Number(currentTabTotal)  > 100000) {
       toastHelper.showToast({
        severity: "warn",
        title: "Export limit reached",
        content: `Your record selection ${currentTabTotal} rows exceeds the export limit of 100,000 rows. Refine your filters and try again.`,
        options: {
          position: "top-center",
          autoClose: 5000,
        }
      });
      return;
    }
    try {
      setDownloadInProgress(true);

      toastHelper.showToast({
        severity: "info",
        title: "Processing",
        content: "Export in progress...",
        options: {
          position: "top-center",
          autoClose: 500,
        }
      });
      const doc = new jsPDF({ compress: true, unit: "pt", format: "a4", orientation: "landscape" });

      const UNICODE_FONT_NAME = "NotoSansHebrew";
      let unicodeFontLoaded = false;
      try {
        const fontResponse = await fetch(`${process.env.PUBLIC_URL}/fonts/NotoSansHebrew-Regular.ttf`);
        const contentType = fontResponse.headers.get("content-type") ?? "";
        if (fontResponse.ok && !contentType.includes("text/html")) {
          const fontBuffer = await fontResponse.arrayBuffer();
          const bytes = new Uint8Array(fontBuffer);
          let binary = "";
          for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          const fontBase64 = window.btoa(binary);
          doc.addFileToVFS("NotoSansHebrew-Regular.ttf", fontBase64);
          doc.addFont("NotoSansHebrew-Regular.ttf", UNICODE_FONT_NAME, "normal");
          unicodeFontLoaded = true;
        }
      } catch (e) {
        console.warn("Hebrew font failed to load:", e);
      }

      const data = await exportRecordData();
      const tableRows = formatRowsForPDF(data, selectedHeaders);

      const fontStyles = unicodeFontLoaded ? { font: UNICODE_FONT_NAME } : {};

      (doc as any).autoTable({
        ...defaultAutoTableOptions,
        head: [selectedHeaders],
        body: tableRows,
        tableWidth: "auto",
        styles: {
          ...defaultAutoTableOptions.styles,
          ...fontStyles,
          cellWidth: "auto",
          overflow: "linebreak",
        },
      });
      const fileName = generateExportFileName("pdf");

      const blob = doc.output("blob");
      await saveBlob(blob, fileName);
      await new Promise(r => setTimeout(r, 0));
      setDownloadInProgress(false);

      toastHelper.showToast({
          severity: "success",
          content: `Export successful: ${fileName}`,
          options: {
            position: "top-center",
            autoClose: 500,
          }
      });
    } catch (error) {
      console.error(error);
      setDownloadInProgress(false);
      toastHelper.showToast({
        severity: "error",
        content: "Export failed due to server error. Please try again.",
        options: {
          position: "top-center",
          autoClose: 5000,
        }
      });
    }
  };

  const addToNeedReview = async (recordSampleState: any) => {
    const userData = getSessionData();
    const dynamicToast = new ToastHelper(true, "Please wait...");
    dynamicToast.updateToast({
      severity: "info",
      content: "Sample generation in progress...",
      title: "Processing",
      showClose: false,
      options: {
        isLoading: true,
        position: "top-center",
        autoClose: 3000,
      },
    });
    try {
      await getSampleRecordData(recordSampleState, userData);

      setActiveTab(0);
      dynamicToast.updateToast({
        severity: "success",
        content: `Sample ${recordSampleState.sampleId} generated successfully`,
        options: {
          onClose: () => dispatch(setRefresh(true)),
        },
      });
      if (nrPoll) nrPoll();
    } catch (error) {
      console.error(error);
      dynamicToast.updateToast({
        severity: "error",
        content:
          "Sample generation failed due to server error. Please try again.",
      });
    }
  };

  const handleExportClick = () => {
    setExportOpen(true);
  };

  useEffect(() => {
    if (refresh) {
      if (allPoll) allPoll();
      if (nrPoll) nrPoll();
      if (nrkPoll) nrkPoll();
    }
  }, [refresh, allPoll, nrPoll, nrkPoll]);

  const handleComponentUnmount = useCallback(() => {
    setOpen(true);
    dispatch(setSelectedRecord(""));
    dispatch(resetState());
  }, [dispatch]);

  useEffect(() => {
    return () => handleComponentUnmount();
  }, [handleComponentUnmount]);

  // Distribute backend results to the correct tab based on record status
  const nrBackend = useMemo(() => backendResults.filter(r => r.status === "Pending Review"), [backendResults]);
  const allBackend = useMemo(() => backendResults.filter(r => r.status === "Reviewed"), [backendResults]);
  const nrkBackend = useMemo(() => backendResults.filter(r => r.status === "Available for Review"), [backendResults]);

  const tabConfig = useMemo(
    () =>
      tabsConfig(
        limits,
        nrLoading,
        nrTotal,
        allLoading,
        allTotal,
        nrkLoading,
        nrkTotal,
        nrRecordWithSearch.length === 0 && nrBackend.length > 0 ? nrBackend : nrRecordWithSearch,
        allRecordWithSearch.length === 0 && allBackend.length > 0 ? allBackend : allRecordWithSearch,
        nrkRecordWithSearch.length === 0 && nrkBackend.length > 0 ? nrkBackend : nrkRecordWithSearch,
        setLimits
      ),
    [
      limits,
      nrLoading,
      nrTotal,
      allLoading,
      allTotal,
      nrkLoading,
      nrkTotal,
      nrRecordWithSearch,
      allRecordWithSearch,
      nrkRecordWithSearch,
      nrBackend,
      allBackend,
      nrkBackend,
    ]
  );

  //To Determine disable state in action button (Generate/ Export)
  const isExportDisabled = useMemo(() => {
    switch (activeTab) {
      case 0:
        return nrTotal === 0;
      case 1:
        return allTotal === 0;
      case 2:
        return nrkTotal === 0;
      default:
        return true;
    }
  }, [activeTab, nrTotal, allTotal, nrkTotal]);
  const isGenerateDisabled = useMemo(() => {
    if (activeTab === 2) {
      return nrkTotal === 0;
    }
    return true;
  }, [activeTab, nrkTotal]);

  //Filter logic to select active tab( with filter and search)
  useEffect(() => {
    if (
      (selectedFilter?.selectedRisk.length === 1 &&
        selectedFilter?.selectedRisk.includes("NR")) ||
      (selectedFilter?.selectedStatus.length === 1 &&
        selectedFilter?.selectedStatus.includes("Available for Review"))
    ) {
      setActiveTab(2);
    }

    if (
      (selectedFilter?.selectedStatus.length === 1 &&
        selectedFilter?.selectedStatus.includes("Pending Review"))
    ) {
      setActiveTab(0);
    }

    if (
      selectedFilter?.selectedEvaluation.length === 2 ||
      selectedFilter?.selectedRisk.length === 5 ||
      selectedFilter?.selectedStatus.length === 3 ||
      (selectedFilter?.selectedStatus.length === 1 &&
        selectedFilter?.selectedStatus.includes("Reviewed"))
    ) {
      setActiveTab(1);
    }
  }, [
    selectedFilter.selectedRisk,
    selectedFilter.selectedStatus,
    selectedFilter.selectedEvaluation,
  ]);

  return (
    <>
      <div className="recordContainer">
        <Typography variant="h4" className="title" data-testid="record-title">
          Text Sentinel
        </Typography>

        <Box className="recordFilter">
          <FilterList />
          <RecordNavbar
            searchData={searchData}
            disabled={{
              export: isExportDisabled || userInfo?.role?.includes("read-only") || downloadInProgress,
              generate:
                isGenerateDisabled || userInfo?.role?.includes("read-only"),
            }}
            handleExportClick={handleExportClick}
            handleGenerateSummary={handleGenerateSummary}
            onSearchChange={setSearchData}
            onSearchSubmit={handleSearchSubmit}
          />
          <Box className="tabHolder">
            <CustomTabs
              activeTab={activeTab}
              name="records"
              tabConfig={tabConfig}
              onTabChange={setActiveTab}
            />
          </Box>
        </Box>
        <Suspense fallback={<>Loading...</>}>
          <ExportRecord
            open={exportOpen}
            closeModal={() => setExportOpen(false)}
            downloadCSV={downloadCSV}
            downloadPDF={downloadPDF}
            requiredColumns={requiredColumns}
          />
          <GenerateSample
            open={isOpen}
            closeModal={() => setIsOpen(false)}
            filteredRecordCount={nrkTotal}
            addToNeedReview={addToNeedReview}
          />
        </Suspense>
        <Suspense fallback={<>Loading...</>}>
          {selectedRecordId !== null && selectedRecordId !== "" && (
            <RecordDetails />
          )}
        </Suspense>
      </div>
      <GettingReady
        open={open}
        handleFilterSubmit={handleFilterSubmit}
        refresh={refresh}
      />
    </>
  );
};

export default Record;
